<script setup>
import ErrorNotice from '../components/feedback/ErrorNotice.vue'
import { onBeforeUnmount, ref, watch } from 'vue'
import { activityApi } from '../api'
import ActivityDetailModal from '../components/modals/ActivityDetailModal.vue'
import ActivityHistoryRecovery from '../components/activities/ActivityHistoryRecovery.vue'
import { memberSession } from '../api/session'
import { useWebActivity } from '../services/activities/webAdapter.js'

const props = defineProps({ activityId: { type: String, default: '' } })
const emit = defineEmits(['navigate', 'login'])
const categories = ref([])
const tag = ref('')
const activities = ref([])
const activity = ref(null)
const loading = ref(false)
const error = ref('')
const recoveringHistory = ref(false)
const { getAuth, isLoggedIn } = useWebActivity()
let sequence = 0
async function load() {
  const current = ++sequence
  recoveringHistory.value = false
  loading.value = true; error.value = ''; activity.value = null; activities.value = []
  try {
    if (props.activityId) {
      const result = await activityApi.detail(props.activityId)
      if (current === sequence) {
        if (!result?.id) throw new Error('活动不存在或已结束')
        activity.value = result
      }
    } else {
      const [groups, rows] = await Promise.all([activityApi.categories(), activityApi.list(tag.value)])
      if (current !== sequence) return
      if (!Array.isArray(groups) || !Array.isArray(rows)) throw new Error('活动加载失败，请重试')
      categories.value = groups; activities.value = rows
    }
  } catch (cause) { if (current === sequence) error.value = cause.message }
  finally { if (current === sequence) loading.value = false }
}
watch([() => props.activityId, tag, memberSession, () => isLoggedIn?.value, () => getAuth()?.memberId], load, { immediate: true })
onBeforeUnmount(() => { ++sequence })
</script>
<template>
  <div class="container activity-route-page">
    <nav v-if="!activity" class="activity-toolbar" aria-label="活动页面导航">
      <button type="button" class="activity-back" @click="emit('navigate', props.activityId ? '/activity/discount' : '/')">
        <img src="/icons/phosphor/arrow-right.svg" alt="" />{{ props.activityId ? '优惠活动' : '首页' }}
      </button>
      <div class="activity-toolbar-actions">
        <button type="button" class="activity-button" @click="emit('navigate', '/mine/prizes')"><img src="/icons/phosphor/gift.svg" alt="" />我的奖品</button>
        <button type="button" class="activity-button" :disabled="loading" @click="load"><img src="/icons/phosphor/arrow-clockwise.svg" alt="" />刷新</button>
      </div>
    </nav>
    <header v-if="!activity" class="activity-page-heading">
      <h1>{{ activity?.title || (activityId ? '活动详情' : '优惠活动') }}</h1>
      <p>{{ activityId ? '查看活动内容、参与规则与奖励记录' : '发现优惠活动，查看专属奖励' }}</p>
    </header>
    <section v-if="loading" class="activity-state activity-loading" role="status" aria-live="polite" aria-busy="true">
      <span class="activity-loading-mark" aria-hidden="true"></span>
      <h2>活动加载中…</h2><p>正在获取活动内容，请稍候</p>
    </section>
    <template v-else-if="error">
      <section class="activity-load-error">
        <div class="activity-state">
          <div class="activity-state-symbol" aria-hidden="true"><img src="/icons/phosphor/ticket.svg" alt="" /></div>
          <h2>暂时无法查看活动</h2>
          <ErrorNotice class="request-error" :message="error"></ErrorNotice>
          <button type="button" class="activity-button activity-button-primary" @click="load">重试</button>
        </div>
        <aside v-if="activityId" class="activity-history-entry">
          <span class="activity-history-icon" aria-hidden="true"><img src="/icons/phosphor/receipt.svg" alt="" /></span>
          <div><h3>曾经参与过这个活动？</h3><p>你仍可查询本人的原申请与奖励记录</p></div>
          <button v-if="!recoveringHistory" type="button" class="activity-button" @click="recoveringHistory = true">查询原参与记录</button>
          <span v-else class="activity-history-open" role="status">原参与记录已展开</span>
        </aside>
      </section>
      <ActivityHistoryRecovery v-if="activityId && recoveringHistory" :activity-id="activityId" />
    </template>
    <template v-else-if="activity">
      <ActivityDetailModal :activity="activity" @close="emit('navigate', '/activity/discount')" />
    </template>
    <template v-else>
      <nav class="activity-categories" aria-label="活动分类"><button v-for="category in categories" :key="category.activityTag" type="button" class="activity-button" :aria-pressed="tag === category.activityTag" @click="tag = category.activityTag">{{ category.name }}</button></nav>
      <div v-if="activities.length" class="activity-grid"><article v-for="item in activities" :key="item.id" class="activity-card"><img v-if="item.coverUrl" class="activity-cover" :src="item.coverUrl" :alt="item.title" loading="lazy" /><div class="activity-card-copy"><p>{{ categories.find((category) => category.activityTag === item.activityTag)?.name || '' }}</p><h2>{{ item.title }}</h2><button type="button" class="activity-button activity-button-primary" @click="emit('navigate', `/activity/${item.id}`)">查看活动与奖品</button></div></article></div>
      <section v-else class="activity-state" role="status"><div class="activity-state-symbol" aria-hidden="true"><img src="/icons/phosphor/gift.svg" alt="" /></div><h2>当前分类暂无活动</h2><p>可以切换其他分类，或稍后再来看看</p></section>
    </template>
  </div>
</template>

<style scoped>
.activity-route-page { --activity-brand: #1878c9; min-height: calc(100dvh - 280px); padding-top: 108px; padding-bottom: 64px; color: var(--ink, #0b1c34); }
.activity-toolbar, .activity-toolbar-actions { display: flex; align-items: center; gap: 12px; }
.activity-toolbar { justify-content: space-between; margin-bottom: 28px; }
.activity-back { display: inline-flex; align-items: center; gap: 8px; padding: 10px 0; color: #566b85; background: transparent; font-size: 14px; }
.activity-back img { width: 18px; height: 18px; transform: rotate(180deg); }
.activity-button { display: inline-flex; justify-content: center; align-items: center; gap: 8px; min-height: 42px; padding: 10px 18px; border: 1px solid #dce6f0; border-radius: 8px; color: #415b79; background: #fff; font-size: 14px; font-weight: 500; line-height: 1.5; cursor: pointer; transition: background .18s, border-color .18s, box-shadow .18s; }
.activity-button img { width: 18px; height: 18px; opacity: .6; }
.activity-button:hover { border-color: #a8c7e2; background: #f0f7ff; }
.activity-button:active { background: #e3f0fc; }
.activity-button:disabled { opacity: .5; cursor: wait; }
.activity-button-primary { border-color: var(--activity-brand); color: #fff; background: var(--activity-brand); }
.activity-button-primary:hover { border-color: #1266ad; background: #1266ad; box-shadow: 0 4px 12px #1878c91a; }
.activity-back:hover { color: var(--activity-brand); }
.activity-button:focus-visible, .activity-back:focus-visible { outline: 3px solid #80b8e5; outline-offset: 4px; }
.activity-page-heading { margin-bottom: 26px; }
.activity-page-heading h1 { margin: 0; font-size: 28px; font-weight: 600; line-height: 1.4; overflow-wrap: anywhere; }
.activity-page-heading p { margin: 8px 0 0; color: #6e7f98; font-size: 14px; line-height: 1.6; }
.activity-load-error { overflow: hidden; border: 1px solid #e0e9f3; border-radius: 18px; background: #fff; }
.activity-state { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 360px; padding: 48px 24px; border-radius: 18px; background: #fff; text-align: center; }
.activity-state-symbol { display: grid; place-items: center; width: 88px; height: 88px; margin-bottom: 24px; border: 8px solid #f6f9fd; border-radius: 50%; background: #eaf2fb; }
.activity-state-symbol img { width: 38px; height: 38px; opacity: .45; }
.activity-state h2 { margin: 0; font-size: 22px; font-weight: 600; line-height: 1.5; }
.activity-state p { max-width: 560px; margin: 10px 0 0; color: #6e7f98; font-size: 14px; line-height: 1.8; overflow-wrap: anywhere; }
.activity-state > .activity-button { min-width: 120px; margin-top: 24px; }
.activity-history-entry { display: flex; align-items: center; gap: 16px; padding: 24px 32px; border-top: 1px solid #e9eff6; background: #fbfdff; }
.activity-history-icon { display: grid; flex: 0 0 42px; place-items: center; height: 42px; border: 1px solid #e1eaf4; border-radius: 10px; background: #fff; }
.activity-history-icon img { width: 22px; height: 22px; opacity: .55; }
.activity-history-entry h3 { margin: 0; color: #415b79; font-size: 14px; font-weight: 500; line-height: 1.6; }
.activity-history-entry p { margin: 3px 0 0; color: #6e7f98; font-size: 13px; line-height: 1.6; }
.activity-history-entry > .activity-button, .activity-history-open { flex-shrink: 0; margin-left: auto; }
.activity-history-open { color: #6e7f98; font-size: 13px; }
.activity-loading-mark { width: 36px; height: 36px; margin-bottom: 24px; border: 3px solid #e1edf8; border-top-color: var(--activity-brand); border-radius: 50%; animation: activity-loading 1s linear infinite; }
@keyframes activity-loading { to { transform: rotate(360deg); } }
.activity-categories { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 24px; }
.activity-categories [aria-pressed='true'] { border-color: #aacde9; color: var(--activity-brand); background: #eaf4ff; }
.activity-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px; }
.activity-card { display: flex; flex-direction: column; overflow: hidden; border: 1px solid #e0e9f3; border-radius: 16px; background: #fff; }
.activity-cover { display: block; width: 100%; aspect-ratio: 16 / 7; object-fit: cover; }
.activity-card-copy { display: flex; flex: 1; flex-direction: column; align-items: flex-start; padding: 24px; }
.activity-card-copy p { margin: 0; color: #6e7f98; font-size: 13px; }
.activity-card-copy h2 { margin: 8px 0 24px; font-size: 20px; line-height: 1.5; overflow-wrap: anywhere; }
.activity-card-copy .activity-button { margin-top: auto; }
@media (max-width: 999px) { .activity-route-page { padding-top: 98px; } }
@media (max-width: 600px) {
  .activity-route-page { padding-top: 88px; padding-bottom: 32px; }
  .activity-toolbar { gap: 8px; margin-bottom: 22px; }
  .activity-toolbar-actions { gap: 8px; }
  .activity-toolbar .activity-button { gap: 6px; padding: 9px 12px; font-size: 13px; }
  .activity-toolbar .activity-button img { width: 16px; height: 16px; }
  .activity-page-heading h1 { font-size: 24px; }
  .activity-state { min-height: 320px; padding: 36px 20px; }
  .activity-state h2 { font-size: 20px; }
  .activity-history-entry { flex-wrap: wrap; gap: 12px; padding: 20px; }
  .activity-history-entry > div { flex: 1; min-width: 0; }
  .activity-history-entry > .activity-button, .activity-history-open { justify-content: center; width: 100%; margin: 4px 0 0; text-align: center; }
  .activity-grid { grid-template-columns: minmax(0, 1fr); gap: 16px; }
}
@media (prefers-reduced-motion: reduce) { .activity-button { transition: none; } .activity-loading-mark { animation: none; } }
</style>
